import { apiClient } from '../apiClient';
import type { ApiJobDescriptionSummary } from './jobDescriptionService';

export type ApiApplicationStage =
  | 'NEW'
  | 'SCREENING'
  | 'INTERVIEW'
  | 'OFFER'
  | 'HIRED'
  | 'REJECTED'
  | 'TALENT_POOL';

/** Stages a Talent Pool candidate can restart a pipeline at. */
export type ApiPipelineStage = Exclude<ApiApplicationStage, 'REJECTED' | 'TALENT_POOL'>;

export type ApiTalentPoolReason =
  | 'JD_ON_HOLD'
  | 'POSITION_FILLED'
  | 'BETTER_FIT_OTHER_ROLE'
  | 'RECONNECT_LATER'
  | 'OTHER';

/** Manager / team lead CV screening result: PASS -> Interview, FAIL -> Rejected, PENDING -> Talent Pool. */
export type ApiCvEvaluationResult = 'PASS' | 'FAIL' | 'PENDING';

export type ApiCvEvaluation = {
  id: string;
  result: ApiCvEvaluationResult;
  from_stage: ApiApplicationStage;
  to_stage: ApiApplicationStage;
  evaluated_at: string;
  evaluated_by: { id: string; display_name: string | null };
};

export type CvEvaluationPayload = {
  result: ApiCvEvaluationResult;
  talent_pool_reason?: ApiTalentPoolReason;
  talent_pool_note?: string;
};

export type ApiAiStatus = 'PENDING' | 'SCORED' | 'FAILED';
export type ApiExtractionStatus = 'WAITING' | 'READY' | 'FAILED' | 'DUPLICATED';
export type ApiRejectionType = 'AI' | 'INTERVIEW' | 'OFFER';

export type PipelineBoardQuery = {
  jd_id?: string;
  duplicate_only?: boolean;
  ai_status?: ApiAiStatus;
  search?: string;
  stage?: ApiApplicationStage;
};

export type ApiApplicationCard = {
  id: string;
  resumesId: string;
  candidate: {
    id: string;
    full_name?: string | null;
    primary_email?: string | null;
    primary_phone?: string | null;
    date_of_birth?: string | null;
  };
  job_description: {
    id: string;
    title: string;
    department: string | null;
  };
  current_stage?: ApiApplicationStage | null;
  ai_status: ApiAiStatus;
  ai_match_score?: number | null;
  years_of_experience?: number | null;
  note_count?: number;
  latest_note_preview?: string | null;
  source?: string | null;
  channel?: string | null;
  next_interview_at?: string | null;
  next_interview_duration_minutes?: number | null;
  next_interview_id?: string | null;
  next_interview_result?: 'PASSED' | 'FAILED' | null;
  next_interview_round?: string | null;
  is_duplicate_candidate: boolean;
  email_sent_at?: string | null;
  can_send_rejection_email?: boolean;
  applied_at: string;
  talent_pool_reason?: ApiTalentPoolReason | null;
  talent_pool_note?: string | null;
  latest_cv_evaluation?: {
    result: ApiCvEvaluationResult;
    evaluated_at: string;
    evaluated_by_name: string | null;
  } | null;
};

export type PipelineBoardResponse = {
  filters: {
    jd_id?: string | null;
    jd_title?: string | null;
    duplicate_only?: boolean;
    ai_status?: ApiAiStatus | null;
    search?: string | null;
    stage?: ApiApplicationStage | null;
  };
  summary?: {
    total?: number;
    duplicate_count?: number;
    pending_ai_count?: number;
  };
  columns: Array<{
    stage: ApiApplicationStage;
    count: number;
    items: ApiApplicationCard[];
  }>;
  available_stages?: ApiApplicationStage[];
};

export type MoveStagePayload = {
  to_stage: ApiApplicationStage;
  talent_pool_reason?: ApiTalentPoolReason;
  talent_pool_note?: string;
};

export type TalentPoolTransferPayload = {
  jd_id: string;
  to_stage: ApiPipelineStage;
  /** Required when restarting at HR Approved (SCREENING). */
  reviewer_emails?: string[];
};

export type ApiApplicationNote = {
  id: string;
  note_text: string;
  created_by: {
    id: string;
    display_name: string;
  };
  updated_by?: {
    id: string;
    display_name: string;
  } | null;
  created_at: string;
  updated_at: string;
};

export type ApiApplicationHistoryItem = {
  application_id: string;
  jd: {
    id: string;
    title: string;
  };
  applied_at: string;
  matching_score: number | null;
  final_stage: ApiApplicationStage;
  note: string | null;
  resume_id: string;
  original_file_name: string | null;
  is_duplicate_candidate: boolean;
};

export type ApiApplicationDetail = {
  id: string;
  candidate: {
    id: string;
    full_name?: string | null;
    primary_email?: string | null;
    primary_phone?: string | null;
    date_of_birth?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  job_description: ApiJobDescriptionSummary;
  resume: {
    id: string;
    original_file_name: string;
    file_extension?: string | null;
    file_size?: number | null;
    extraction_status: ApiExtractionStatus;
    source?: string | null;
    channel?: string | null;
    extracted_at?: string | null;
    created_at: string;
    updated_at: string;
  };
  current_stage?: ApiApplicationStage | null;
  ai_status: ApiAiStatus;
  ai_match_score?: number | null;
  years_of_experience?: number | null;
  skills?: Array<{
    skill: string;
    years: number;
  }>;
  ai_strengths?: string[];
  ai_gaps?: string[];
  ai_summary?: string | null;
  ai_reject_reason?: string | null;
  is_duplicate_candidate: boolean;
  rejection_type?: ApiRejectionType | null;
  email_sent_at?: string | null;
  notes?: ApiApplicationNote[];
  application_history?: ApiApplicationHistoryItem[];
  applied_at: string;
  stage_updated_at?: string | null;
  cv_evaluations?: ApiCvEvaluation[];
  /** CV reviewers HR assigned when moving the CV to HR Approved. */
  assigned_reviewers?: Array<{ email: string; name: string | null; assigned_at: string }>;
  /** The signed-in CV reviewer may evaluate it now (assigned and in HR Approved). */
  can_evaluate?: boolean;
};

export type ApiReviewerOptions = {
  items: Array<{ email: string; name: string; covers_jd: boolean }>;
  default_emails: string[];
};

export type ApplicationNotePayload = {
  note_text: string;
};

export const getPipelineBoard = (params?: PipelineBoardQuery) =>
  apiClient.get<PipelineBoardResponse>('/pipeline', params);

export const getApplicationDetail = (applicationId: string) =>
  apiClient.get<ApiApplicationDetail>(`/applications/${applicationId}`);

export const deleteApplication = (applicationId: string) =>
  apiClient.delete<void>(`/applications/${applicationId}`);

export const moveApplicationStage = (applicationId: string, payload: MoveStagePayload) =>
  apiClient.patch<ApiApplicationCard>(`/applications/${applicationId}/stage`, payload);

/** Reviewer suggestions for the application's JD, or for `jdId` (Talent Pool move to another JD). */
export const getReviewerOptions = (applicationId: string, jdId?: string) =>
  apiClient.get<ApiReviewerOptions>(
    `/applications/${applicationId}/reviewer-options`,
    jdId ? { jd_id: jdId } : undefined,
  );

/** NEW -> HR Approved with the picked CV reviewers (they get notified). */
export const hrApproveApplication = (applicationId: string, reviewerEmails: string[]) =>
  apiClient.post<ApiApplicationCard>(`/applications/${applicationId}/hr-approve`, {
    reviewer_emails: reviewerEmails,
  });

export const createCvEvaluation = (applicationId: string, payload: CvEvaluationPayload) =>
  apiClient.post<ApiApplicationCard>(`/applications/${applicationId}/cv-evaluations`, payload);

export const transferFromTalentPool = (applicationId: string, payload: TalentPoolTransferPayload) =>
  apiClient.post<ApiApplicationCard>(
    `/applications/${applicationId}/talent-pool/transfer`,
    payload,
  );

export const createApplicationNote = (applicationId: string, payload: ApplicationNotePayload) =>
  apiClient.post<ApiApplicationNote>(`/applications/${applicationId}/notes`, payload);

export const updateApplicationNote = (
  applicationId: string,
  noteId: string,
  payload: ApplicationNotePayload,
) => apiClient.patch<ApiApplicationNote>(`/applications/${applicationId}/notes/${noteId}`, payload);
